/**
 * ProtocolQuickMatchCard Component
 *
 * Displays quick match results for a single protocol in the multi-study selection UI.
 * Shows how well a patient might match each protocol based on prior analysis,
 * how many criteria can be reused vs. need new analysis, and estimated cost.
 */

import { Check, RefreshCw, X, HelpCircle } from 'lucide-react';

export type MatchLevel = 'likely_eligible' | 'review_needed' | 'likely_excluded' | 'no_data';

export interface ProtocolQuickMatchCardProps {
  protocolId: string;
  protocolName: string;
  matchLevel: MatchLevel;
  reusableCriteriaCount: number;
  newAnalysisCriteriaCount: number;
  totalCriteriaCount: number;
  estimatedCost: number;
  selected: boolean;
  onToggleSelect: () => void;
}

// Match level styling and display configuration
const MATCH_LEVEL_STYLES: Record<
  MatchLevel,
  {
    bg: string;
    text: string;
    border: string;
    icon: typeof Check;
    label: string;
  }
> = {
  likely_eligible: {
    bg: 'bg-green-100 dark:bg-green-900/40',
    text: 'text-green-800 dark:text-green-300',
    border: 'border-green-200 dark:border-green-800',
    icon: Check,
    label: 'Likely Eligible',
  },
  review_needed: {
    bg: 'bg-amber-100 dark:bg-amber-900/40',
    text: 'text-amber-800 dark:text-amber-300',
    border: 'border-amber-200 dark:border-amber-800',
    icon: RefreshCw,
    label: 'Review Needed',
  },
  likely_excluded: {
    bg: 'bg-red-100 dark:bg-red-900/40',
    text: 'text-red-800 dark:text-red-300',
    border: 'border-red-200 dark:border-red-800',
    icon: X,
    label: 'Likely Excluded',
  },
  no_data: {
    bg: 'bg-gray-100 dark:bg-gray-700',
    text: 'text-gray-600 dark:text-gray-400',
    border: 'border-gray-200 dark:border-gray-700',
    icon: HelpCircle,
    label: 'No Data',
  },
};

export function ProtocolQuickMatchCard({
  protocolId,
  protocolName,
  matchLevel,
  reusableCriteriaCount,
  newAnalysisCriteriaCount,
  totalCriteriaCount,
  estimatedCost,
  selected,
  onToggleSelect,
}: ProtocolQuickMatchCardProps) {
  const matchStyle = MATCH_LEVEL_STYLES[matchLevel];
  const MatchIcon = matchStyle.icon;

  // Calculate progress percentage for reusable criteria
  const reusablePercentage =
    totalCriteriaCount > 0 ? Math.round((reusableCriteriaCount / totalCriteriaCount) * 100) : 0;

  // Format cost display
  const formattedCost = estimatedCost.toFixed(2);

  return (
    <div
      onClick={onToggleSelect}
      className={`
        relative bg-white dark:bg-gray-800 rounded-xl border-2 p-4 cursor-pointer transition-all
        ${
          selected
            ? 'border-primary-500 bg-primary-50/30 dark:bg-primary-900/20 shadow-md'
            : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-sm'
        }
      `}
      role="button"
      aria-pressed={selected}
      aria-label={`${protocolName} - ${matchStyle.label}. ${reusableCriteriaCount} of ${totalCriteriaCount} criteria reusable. Estimated cost $${formattedCost}. ${selected ? 'Selected' : 'Not selected'}`}
    >
      {/* Selection checkbox - positioned in top right */}
      <div className="absolute top-4 right-4">
        <div
          className={`
            w-5 h-5 rounded border-2 flex items-center justify-center transition-colors
            ${
              selected
                ? 'bg-primary-600 border-primary-600'
                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-600'
            }
          `}
        >
          {selected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
        </div>
      </div>

      {/* Protocol name header */}
      <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 pr-8 mb-3">{protocolName}</h3>

      {/* Match level badge */}
      <div className="mb-4">
        <span
          className={`
            inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full
            ${matchStyle.bg} ${matchStyle.text} ${matchStyle.border} border
          `}
        >
          <MatchIcon size={14} />
          {matchStyle.label}
        </span>
      </div>

      {/* Criteria reuse stats */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-1.5">
          <span className="text-gray-600 dark:text-gray-400">
            <span className="font-medium text-gray-900 dark:text-gray-100">{reusableCriteriaCount}</span> of{' '}
            {totalCriteriaCount} criteria reusable
          </span>
          <span className="text-gray-500 dark:text-gray-400">{reusablePercentage}%</span>
        </div>

        {/* Progress bar */}
        <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary-500 rounded-full transition-all duration-300"
            style={{ width: `${reusablePercentage}%` }}
          />
        </div>

        {/* New analysis count hint */}
        {newAnalysisCriteriaCount > 0 && (
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
            {newAnalysisCriteriaCount} criteria need new analysis
          </p>
        )}
      </div>

      {/* Estimated cost */}
      <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 dark:text-gray-400">Estimated cost</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">${formattedCost}</span>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">for full evaluation</p>
      </div>
    </div>
  );
}
